import {createSlice} from '@reduxjs/toolkit';
import {deleteRegisteredBrowserSuccess} from 'src/openapi/admin/apis/DeleteRegisteredBrowserApi';
import {getUserActivityFailure, getUserActivitySuccess} from 'src/openapi/admin/apis/GetUserActivityApi';
import {getUserBrowserInfoSuccess} from 'src/openapi/admin/apis/GetUserBrowserInfoApi';
import {getUserInfoSuccess} from 'src/openapi/admin/apis/GetUserInfoApi';
import {saveBrowserRegistrationSettingsSuccess} from 'src/openapi/admin/apis/SaveBrowserRegistrationSettingsApi';

import type {PayloadAction} from '@reduxjs/toolkit';
import type {UserActivityInfo} from 'src/openapi/admin/models/UserActivityInfo';
import type {UserBrowserInfo} from 'src/openapi/admin/models/UserBrowserInfo';
import type {UserProfileInfo} from 'src/openapi/admin/models/UserProfileInfo';
import type {AppState} from 'src/store/typedef';
import type {ServerResponse} from 'src/typedef';

type InitialState = {
    information: UserProfileInfo | null,
    activity: ServerResponse<UserActivityInfo> | null,
    browserInfo: UserBrowserInfo | null,
    showRegisterBrowserModal: boolean,
};

const initialState: InitialState = {
    information: null,
    activity: null,
    browserInfo: null,
    showRegisterBrowserModal: false,
};

export const userProfileSlice = createSlice({
    name: 'userProfile',
    initialState,
    reducers: {
        clearUserProfileTab: state => {
            state.information = null;
            state.activity = null;
            state.browserInfo = null;
        },
        clearUserInformation: state => {
            state.information = null;
        },
        toggleBrowserLimitsModal: (state, action: PayloadAction<boolean>) => {
            state.showRegisterBrowserModal = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getUserInfoSuccess, (state, action) => {
                state.information = action.payload.data;
            })
            .addCase(getUserActivitySuccess, (state, action) => {
                state.activity = {data: action.payload.data};
            })
            .addCase(getUserActivityFailure, (state, action) => {
                state.activity = {error: action.payload.data};
            })
            .addCase(getUserBrowserInfoSuccess, (state, action) => {
                state.browserInfo = action.payload.data;
            })
            .addCase(saveBrowserRegistrationSettingsSuccess, (state, action) => {
                state.browserInfo = {
                    ...state.browserInfo,
                    enabled: action.payload.meta.browserRegistrationSettingsSaveRequest.registrationEnforced,
                };
            })
            .addCase(deleteRegisteredBrowserSuccess, (state, action) => {
                if (state.browserInfo) {
                    state.browserInfo = {
                        enabled: state.browserInfo.enabled,
                        places: state.browserInfo.places?.filter(({browserId}) =>
                            browserId !== action.payload.meta.userPlaceId,
                        ),
                    };
                }
            });
    },
});

type SelectUserInformation = (state: AppState) => UserProfileInfo | null;
export const selectUserInformation: SelectUserInformation = state => state.userPage.tabs.userProfile.information;

type SelectUserBrowserInfo = (state: AppState) => UserBrowserInfo | null;
export const selectUserBrowserInfo: SelectUserBrowserInfo = state => state.userPage.tabs.userProfile.browserInfo;
export const selectRegisterBrowserModal: (state: AppState) => boolean = state =>
    state.userPage.tabs.userProfile.showRegisterBrowserModal;

type SelectUserActivity = (state: AppState) => ServerResponse<UserActivityInfo> | null;
export const selectUserActivity: SelectUserActivity = state => state.userPage.tabs.userProfile.activity;

export const {
    clearUserProfileTab,
    clearUserInformation,
    toggleBrowserLimitsModal,
} = userProfileSlice.actions;
